import React, { useState, useCallback } from "react"
import { Phone, PhoneCall, PhoneIncoming, PhoneSlash } from "@phosphor-icons/react"
import { Button } from "../ui/button"
import { useCallStore, useAuthStore } from "../../stores"
import { cn } from "../../lib/utils"
import { api } from "../../lib/api/client"
import { toast } from "sonner"
import { enableDialing } from "../../lib/twilio"
import { requestNotificationPermission } from "../../lib/twilio/incoming-call-alerts"

export function PhoneStatusIndicator() {
  const { browserDialing, incomingCall, currentCall, agentStatus, isInQueue, setIsInQueue } = useCallStore()
  const { user, updateUser } = useAuthStore()
  const [isToggling, setIsToggling] = useState(false)

  // Toggle queue status or enable phone
  const handleClick = useCallback(async () => {
    if (!user || isToggling) return

    setIsToggling(true)
    try {
      // If already on a call or incoming, just toggle queue status (phone is already connected)
      if (currentCall || incomingCall) {
        const newQueueStatus = !isInQueue
        const updated = await api.users.update(user._id, {
          agent_is_in_queue: newQueueStatus
        })
        if (updated.success || updated.data) {
          setIsInQueue(newQueueStatus)
          updateUser({ agent_is_in_queue: newQueueStatus })
          toast.success(newQueueStatus
            ? "You will receive queue calls after this call"
            : "You will not receive queue calls after this call"
          )
        } else {
          toast.error("Failed to update queue status")
        }
        return
      }

      // If phone not connected or opting in, enable dialing first
      if (browserDialing !== "ready" || !isInQueue) {
        const dialingReady = await enableDialing()
        if (!dialingReady) {
          toast.error("Failed to connect phone. Please try again.")
          return
        }
        requestNotificationPermission()
      }

      // If phone wasn't ready, we're opting in. Otherwise toggle current state.
      const newQueueStatus = browserDialing !== "ready" ? true : !isInQueue
      const updated = await api.users.update(user._id, {
        agent_is_in_queue: newQueueStatus
      })

      if (updated.success || updated.data) {
        setIsInQueue(newQueueStatus)
        updateUser({ agent_is_in_queue: newQueueStatus })
        toast.success(newQueueStatus ? "Now receiving queue calls" : "No longer receiving queue calls")
      } else {
        toast.error("Failed to update queue status")
      }
    } catch (error) {
      toast.error("Failed to update queue status")
    } finally {
      setIsToggling(false)
    }
  }, [user, isToggling, currentCall, incomingCall, browserDialing, isInQueue, setIsInQueue, updateUser])

  // Determine display state
  const getDisplayState = () => {
    if (browserDialing === "not_setup" || browserDialing === "error") {
      return "offline"
    }
    if (browserDialing === "loading" || isToggling) {
      return "loading"
    }
    if (incomingCall) {
      return isInQueue ? "incoming" : "incoming_opted_out"
    }
    if (currentCall) {
      return isInQueue ? "oncall" : "oncall_opted_out"
    }
    // Check if user has opted out of queue
    if (!isInQueue && browserDialing === "ready") {
      return "not_in_queue"
    }
    if (agentStatus === "available") {
      return "ready"
    }
    return "ready"
  }

  const displayState = getDisplayState()

  const stateConfig = {
    offline: {
      icon: PhoneSlash,
      iconClass: "text-muted-foreground",
      buttonClass: "hover:text-foreground hover:bg-muted",
      dotClass: "bg-muted-foreground",
      title: "Phone not connected - Click to enable",
    },
    loading: {
      icon: Phone,
      iconClass: "text-muted-foreground animate-pulse",
      buttonClass: "hover:bg-muted",
      dotClass: "bg-warning animate-pulse",
      title: "Connecting phone...",
    },
    ready: {
      icon: Phone,
      iconClass: "text-success",
      buttonClass: "hover:bg-success/10",
      dotClass: "bg-success",
      title: "Receiving queue calls - Click to opt out",
    },
    not_in_queue: {
      icon: Phone,
      iconClass: "text-danger",
      buttonClass: "hover:bg-danger/10",
      dotClass: "bg-danger",
      title: "Not receiving queue calls - Click to opt in",
    },
    incoming: {
      icon: PhoneIncoming,
      iconClass: "text-purple-600",
      buttonClass: "bg-purple-100 hover:bg-purple-200",
      dotClass: "bg-purple-500 animate-pulse",
      title: "Incoming call - Receiving queue calls after this call - Click to opt out",
    },
    incoming_opted_out: {
      icon: PhoneIncoming,
      iconClass: "text-danger",
      buttonClass: "bg-danger/10 hover:bg-danger/20",
      dotClass: "bg-danger animate-pulse",
      title: "Incoming call - Not receiving queue calls after this call",
    },
    oncall: {
      icon: PhoneCall,
      iconClass: "text-primary",
      buttonClass: "bg-primary/10 hover:bg-primary/20",
      dotClass: "bg-primary animate-badge-pulse",
      title: "On a call - Receiving queue calls after this call - Click to opt out",
    },
    oncall_opted_out: {
      icon: PhoneCall,
      iconClass: "text-danger",
      buttonClass: "bg-danger/10 hover:bg-danger/20",
      dotClass: "bg-danger animate-badge-pulse",
      title: "On a call - Not receiving queue calls after this call",
    },
  }

  const config = stateConfig[displayState]
  const Icon = config.icon

  // Allow clicking when not already toggling
  const canClick = !isToggling && browserDialing !== "loading"

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      className={cn("relative", config.buttonClass)}
      title={config.title}
      onClick={canClick ? handleClick : undefined}
      disabled={isToggling}
    >
      <Icon className={cn("h-4 w-4 transition-colors", config.iconClass)} />
      <span
        className={cn(
          "absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-background transition-colors",
          config.dotClass
        )}
      />
    </Button>
  )
}
